import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLayout } from "../../context/LayoutContext";
import "../../css/Sidebar.css";
import { ReactComponent as Logo } from "../../assets/logotipo_cor.svg";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronLeftOutlinedIcon from "@mui/icons-material/ChevronLeftOutlined";
import { MENU_ITEMS, getActiveGroupPaths } from "../../config/menuItems";

// ============================================================================
// CUSTOM HOOKS
// ============================================================================

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);

    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, [query]);

  return matches;
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const SubMenuItem = ({ item, isActive, sidebarOpen, onClick }) => {
  const { t } = useTranslation();
  const Icon = item.icon;
  return (
    <li
      className={`submenu-item ${isActive ? "active" : ""}`}
      onClick={onClick}
      data-tip={!sidebarOpen ? t(item.text) : undefined}
    >
      {Icon && <Icon className="menu-icon" />}
      <span className="menu-text">{t(item.text)}</span>
    </li>
  );
};

SubMenuItem.displayName = "SubMenuItem";

const MenuItem = ({
  item,
  path,
  sidebarOpen,
  activeLink,
  activeGroupPaths,
  openPaths,
  onToggle,
  onNavigate,
  level = 0,
}) => {
  const { t } = useTranslation();
  const hasSubItems = item.subItems?.length > 0;
  const isForcedOpen = activeGroupPaths.includes(path);
  const isSubMenuOpen = isForcedOpen || openPaths.includes(path);
  const Icon = item.icon;

  const handleToggle = () => {
    if (hasSubItems) {
      if (!isForcedOpen) onToggle(path);
    } else {
      onNavigate(item);
    }
  };

  return (
    <li>
      <div
        className={`submenu-toggle ${isForcedOpen ? "active-group" : ""}`}
        onClick={handleToggle}
        data-tip={!sidebarOpen ? t(item.title || item.text) : undefined}
        style={
          sidebarOpen
            ? { paddingLeft: isForcedOpen ? `calc(${1 + level}rem - 3px)` : `${1 + level}rem` }
            : undefined
        }
      >
        {Icon && <Icon className="menu-icon" />}
        {sidebarOpen && (
          <span className="menu-text menu-text-toggle">
            {t(item.title || item.text)}
          </span>
        )}
        {hasSubItems && sidebarOpen && (
          <KeyboardArrowDownIcon
            className={`submenu-arrow ${isSubMenuOpen ? "open" : ""}`}
          />
        )}
      </div>

      {hasSubItems && isSubMenuOpen && sidebarOpen && (
        <ul className="submenu">
          {item.subItems.map((subItem, index) => {
            const subPath = `${path}-${index}`;

            if (subItem.subItems?.length > 0) {
              return (
                <MenuItem
                  key={subPath}
                  item={subItem}
                  path={subPath}
                  sidebarOpen={sidebarOpen}
                  activeLink={activeLink}
                  activeGroupPaths={activeGroupPaths}
                  openPaths={openPaths}
                  onToggle={onToggle}
                  onNavigate={onNavigate}
                  level={level + 1}
                />
              );
            }

            return (
              <SubMenuItem
                key={subPath}
                item={subItem}
                isActive={activeLink === subItem.link}
                sidebarOpen={sidebarOpen}
                onClick={() => onNavigate(subItem)}
              />
            );
          })}
        </ul>
      )}
    </li>
  );
};

MenuItem.displayName = "MenuItem";

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function Sidebar() {
  const { t } = useTranslation();
  const { sidebarOpen, setSidebarOpen, subMenuOpen, setSubMenuOpen, openTab } = useLayout();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const location = useLocation();
  const navigate = useNavigate();

  const activeGroupPaths = useMemo(
    () => getActiveGroupPaths(MENU_ITEMS, location.pathname),
    [location.pathname]
  );

  const toggleSubMenu = useCallback(
    (path) => {
      setSubMenuOpen((prev) =>
        prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
      );
    },
    [setSubMenuOpen]
  );

  const handleNavigate = useCallback(
    (item) => {
      openTab?.(t(item.text), item.link);
      navigate(item.link);
    },
    [openTab, navigate, t]
  );

  const toggleSidebar = useCallback(
    (e) => {
      e.stopPropagation();
      setSidebarOpen(!sidebarOpen);
    },
    [sidebarOpen, setSidebarOpen]
  );

  const sidebarClass = useMemo(
    () =>
      `sidebar ${sidebarOpen ? "open" : "closed"} ${isMobile ? "mobile" : "desktop"
      }`,
    [sidebarOpen, isMobile]
  );

  const handleSidebarClick = useCallback(() => {
    if (!sidebarOpen) setSidebarOpen(true);
  }, [sidebarOpen, setSidebarOpen]);

  return (
    <>
      {isMobile && (
        <button className="menu-toggle-top" onClick={toggleSidebar}>
          {sidebarOpen ? "←" : "☰"}
        </button>
      )}

      <aside className={sidebarClass} onClick={handleSidebarClick}>
        <div className={`sidebar-logo ${sidebarOpen ? "" : "collapsed"}`}>
          {!isMobile && (
            <Logo preserveAspectRatio={sidebarOpen ? "xMidYMid meet" : "xMinYMid slice"} />
          )}
        </div>

        <nav className="sidebar-nav">
          <ul>
            {MENU_ITEMS.map((item, i) => {
              if (item.type === "divider") {
                return sidebarOpen ? (
                  <li key={`divider-${i}`} className="menu-divider" />
                ) : null;
              }
              return (
                <MenuItem
                  key={i}
                  item={item}
                  path={String(i)}
                  sidebarOpen={sidebarOpen}
                  activeLink={location.pathname}
                  activeGroupPaths={activeGroupPaths}
                  openPaths={subMenuOpen}
                  onToggle={toggleSubMenu}
                  onNavigate={handleNavigate}
                />
              );
            })}
          </ul>
        </nav>

        {!isMobile && (
          <button
            className="sidebar-handle"
            onClick={toggleSidebar}
            aria-label={sidebarOpen ? t("sidebar.closeMenu") : t("sidebar.openMenu")}
          >
            <ChevronLeftOutlinedIcon
              className={`sidebar-handle-icon ${sidebarOpen ? "" : "collapsed"}`}
            />
          </button>
        )}
      </aside>
    </>
  );
}
